import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardHeader,
  CardBody,
  Heading,
  List,
  ListItem,
  HStack,
  Text,
  Badge,
  VStack,
  Button,
  Icon,
  Flex,
  useToast,
  Skeleton,
  Container,
  useColorModeValue,
  Avatar,
  AvatarGroup,
  Progress,
  Divider,
  Alert,
  AlertIcon,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Grid,
  GridItem
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiFile, 
  FiImage, 
  FiLayout, 
  FiClock, 
  FiTrendingUp,
  FiUsers,
  FiGlobe,
  FiSettings,
  FiBookOpen,
  FiRefreshCw,
  FiMoreVertical,
  FiEye,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiBook,
  FiCode
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import apiClient from '../../../utils/apiClient';
import { format, parseISO, formatDistanceToNow, subDays, isAfter } from 'date-fns';

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionGrid = motion(SimpleGrid);

const Home = () => {
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const [stats, setStats] = useState([
    { 
      label: 'Total Sites', 
      value: '-', 
      endpoint: 'sites', 
      loading: true,
      icon: FiGlobe,
      color: 'blue',
      change: null
    },
    { 
      label: 'Content Items', 
      value: '-', 
      endpoint: 'content-entries', 
      loading: true,
      icon: FiFile,
      color: 'green',
      change: null
    },
    { 
      label: 'Media Files', 
      value: '-', 
      endpoint: 'assets', 
      loading: true,
      icon: FiImage,
      color: 'purple',
      change: null
    },
    { 
      label: 'Content Models', 
      value: '-', 
      endpoint: 'content-models', 
      loading: true,
      icon: FiLayout,
      color: 'orange',
      change: null
    }
  ]);
  
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Get tenant ID and user info from session storage
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];
  const userName = user?.name || user?.email || 'User';

  const quickActions = [
    { 
      label: 'Create Content', 
      icon: FiFile,
      path: '/content/create',
      color: 'blue',
      description: 'Start writing new content'
    },
    { 
      label: 'Upload Media', 
      icon: FiImage,
      path: '/media/create',
      color: 'green',
      description: 'Add images and files'
    },
    { 
      label: 'New Content Model', 
      icon: FiLayout,
      path: '/content-models/create',
      color: 'purple',
      description: 'Define content structure'
    },
    { 
      label: 'Create Taxonomy', 
      icon: FiSettings,
      path: '/taxonomies/create',
      color: 'orange',
      description: 'Organize with categories'
    }
  ];

  // Enhanced quick actions with documentation links
  const documentationActions = [
    {
      label: 'User Guide',
      icon: FiBook,
      path: '/user-guide',
      color: 'blue',
      description: 'Learn how to use the CMS',
      variant: 'outline'
    },
    {
      label: 'API Documentation',
      icon: FiCode,
      path: '/documentation',
      color: 'green',
      description: 'Technical API reference',
      variant: 'outline'
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Create a stable logActivity callback
  const logActivity = useCallback((type, description, source = 'user') => {
    const activity = {
      id: `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      title: description,
      time: new Date().toISOString(),
      status: 'completed',
      source,
      user: userName
    };

    // Store in localStorage for persistence across sessions
    const existingActivities = JSON.parse(localStorage.getItem('cms_activities') || '[]');
    const updatedActivities = [activity, ...existingActivities].slice(0, 50); // Keep last 50 activities
    localStorage.setItem('cms_activities', JSON.stringify(updatedActivities));
    
    // Update state if component is still mounted
    setActivities(prev => [activity, ...prev].slice(0, 20));
  }, [userName]);

  const setupActivityTracking = useCallback(() => {
    // Track location changes using React Router location
    const currentPath = location.pathname;
    if (currentPath !== '/') { // Don't log initial load of home page
      logActivity('navigation', `Navigated to ${currentPath}`, 'system');
    }

    // Track form submissions
    const handleFormSubmit = (e) => {
      if (e.target.closest('form')) {
        const formAction = e.target.action || window.location.pathname;
        logActivity('form_submit', `Form submitted: ${formAction}`, 'user');
      }
    };

    document.addEventListener('submit', handleFormSubmit);

    // Cleanup function
    return () => {
      document.removeEventListener('submit', handleFormSubmit);
    };
  }, [location.pathname, logActivity]);

  useEffect(() => {
    if (!tenantId) {
      toast({
        title: "Authentication Error",
        description: "No tenant ID found. Please log in again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    fetchAllStats();
    fetchRecentActivities();
    
    // Set up activity tracking
    const cleanup = setupActivityTracking();
    
    // Return cleanup function
    return cleanup;
  }, [tenantId, setupActivityTracking]);

  const fetchAllStats = async () => {
    setIsLoading(true);
    const updatedStats = [...stats];
    
    try {
      const promises = updatedStats.map(stat => fetchStat(stat.endpoint));
      const results = await Promise.allSettled(promises);
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          updatedStats[index].value = result.value.current;
          updatedStats[index].change = result.value.change;
        } else {
          updatedStats[index].value = 'Error';
          console.error(`Failed to fetch ${updatedStats[index].endpoint}:`, result.reason);
        }
        updatedStats[index].loading = false;
      });
      
      setStats(updatedStats);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({
        title: "Error",
        description: "Failed to load dashboard stats. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStat = async (endpoint) => {
    try {
      const response = await apiClient(`/${endpoint}/${tenantId}`);
      
      let current = 0;
      if (Array.isArray(response.data)) {
        current = response.data.length;
      } else if (response.data?.total) {
        current = response.data.total;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        current = response.data.data.length;
      } else {
        current = response.length || 0;
      }

      // Calculate change (mock calculation - you can implement real comparison)
      const change = Math.floor(Math.random() * 20) - 10; // Random change for demo
      
      return { current, change };
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  };

  const fetchRecentActivities = async () => {
    setActivitiesLoading(true);
    
    try {
      // Get stored activities from localStorage
      const storedActivities = JSON.parse(localStorage.getItem('cms_activities') || '[]');
      
      // Fetch composite activities from API
      const apiActivities = await fetchCompositeActivities();
      
      // Combine and sort all activities
      const allActivities = [...storedActivities, ...apiActivities]
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, 15);
      
      setActivities(allActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast({
        title: "Error",
        description: "Failed to load recent activities.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchCompositeActivities = async () => {
    try {
      const contentPromise = apiClient(`/content-entries/${tenantId}?limit=5&sort=updated_at:desc`);
      const mediaPromise = apiClient(`/assets/${tenantId}?limit=5&sort=created_at:desc`);
      const modelsPromise = apiClient(`/content-models/${tenantId}?limit=5&sort=updated_at:desc`);
      
      const [contentRes, mediaRes, modelsRes] = await Promise.allSettled([
        contentPromise, mediaPromise, modelsPromise
      ]);
      
      const activities = [];
      
      // Process API responses and convert to activity format
      if (contentRes.status === 'fulfilled' && contentRes.value?.data?.data) {
        contentRes.value.data.data.forEach(entry => {
          activities.push({
            id: `content-${entry.id}`,
            type: 'content',
            title: entry.title || 'Content Updated',
            entityId: entry.id,
            time: entry.updated_at,
            status: entry.content_status || 'updated',
            path: `/content/edit/${entry.id}`,
            source: 'api'
          });
        });
      }
      
      if (mediaRes.status === 'fulfilled' && mediaRes.value?.data?.data) {
        mediaRes.value.data.data.forEach(media => {
          activities.push({
            id: `media-${media.id}`,
            type: 'media',
            title: media.filename || 'Media Uploaded',
            entityId: media.id,
            time: media.created_at || media.updated_at,
            status: 'uploaded',
            path: `/media/edit/${media.id}`,
            source: 'api'
          });
        });
      }
      
      if (modelsRes.status === 'fulfilled' && modelsRes.value?.data) {
        modelsRes.value.data.forEach(model => {
          activities.push({
            id: `model-${model.id}`,
            type: 'model',
            title: `${model.name} Model Updated`,
            entityId: model.id,
            time: model.updated_at,
            status: 'updated',
            path: `/content-models/edit/${model.id}`,
            source: 'api'
          });
        });
      }
      
      return activities;
    } catch (error) {
      console.error('Error creating composite activities:', error);
      return [];
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'content': return FiFile;
      case 'media': return FiImage;
      case 'model': return FiLayout;
      case 'navigation': return FiEye;
      case 'form_submit': return FiEdit;
      default: return FiClock;
    }
  };
  
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'published': return 'green';
      case 'draft': return 'gray';
      case 'pending': return 'orange';
      case 'uploaded': return 'blue';
      case 'updated': return 'purple';
      case 'completed': return 'green';
      case 'processing': return 'yellow';
      default: return 'gray';
    }
  };
  
  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = parseISO(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'recently';
    }
  };

  const handleQuickAction = (path) => {
    logActivity('quick_action', `Used quick action: ${path}`, 'user');
    navigate(path);
  };
  
  const handleActivityClick = (activity) => {
    if (activity.path) {
      logActivity('activity_click', `Clicked on activity: ${activity.title}`, 'user');
      navigate(activity.path);
    }
  };

  const refreshData = () => {
    logActivity('refresh', 'Refreshed dashboard data', 'user');
    fetchAllStats();
    fetchRecentActivities();
  };

  return (
    <Box minH="100vh" bg={bgColor} py={8}>
      <Container maxW="container.xl">
        <MotionBox
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Welcome Header */}
          <MotionBox variants={itemVariants} mb={8}>
            <Flex justify="space-between" align="center" mb={6}>
              <Box>
                <Heading size="xl" mb={2}>
                  Welcome back, {userName}! 👋
                </Heading>
                <Text color={textColor} fontSize="lg">
                  Here's what's happening with your content today
                </Text>
              </Box>
              <HStack spacing={3}>
                <Tooltip label="Refresh data">
                  <IconButton
                    icon={<FiRefreshCw />}
                    variant="outline"
                    onClick={refreshData}
                    isLoading={isLoading}
                  />
                </Tooltip>
                <Button
                  leftIcon={<FiBook />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => navigate('/user-guide')}
                >
                  User Guide
                </Button>
                <Button
                  leftIcon={<FiCode />}
                  colorScheme="green"
                  variant="outline"
                  onClick={() => navigate('/documentation')}
                >
                  API Docs
                </Button>
              </HStack>
            </Flex>

            <Text fontSize="sm" color={textColor}>
              Last updated: {formatTimeAgo(lastRefresh.toISOString())}
            </Text>
          </MotionBox>

          {/* Enhanced Stats Grid */}
          <MotionGrid 
            variants={itemVariants}
            columns={{ base: 1, md: 2, lg: 4 }} 
            spacing={6} 
            mb={8}
          >
            {stats.map((stat, index) => (
              <MotionCard
                key={stat.label}
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                shadow="lg"
                _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                transition="all 0.3s"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <CardBody>
                  <Flex justify="space-between" align="start">
                    <Stat>
                      <Flex align="center" mb={2}>
                        <Box
                          p={2}
                          borderRadius="lg"
                          bg={`${stat.color}.100`}
                          color={`${stat.color}.600`}
                          mr={3}
                        >
                          <Icon as={stat.icon} boxSize={5} />
                        </Box>
                        <StatLabel fontSize="sm" color={textColor}>
                          {stat.label}
                        </StatLabel>
                      </Flex>
                      <StatNumber fontSize="2xl" fontWeight="bold">
                        {stat.loading ? (
                          <Skeleton height="32px" width="60px" />
                        ) : (
                          stat.value
                        )}
                      </StatNumber>
                      {stat.change !== null && !stat.loading && (
                        <StatHelpText mb={0}>
                          <StatArrow 
                            type={stat.change >= 0 ? 'increase' : 'decrease'} 
                          />
                          {Math.abs(stat.change)}% this week
                        </StatHelpText>
                      )}
                    </Stat>
                  </Flex>
                </CardBody>
              </MotionCard>
            ))}
          </MotionGrid>

          <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={8}>
            {/* Enhanced Recent Activity */}
            <GridItem>
              <MotionCard 
                variants={itemVariants}
                bg={cardBg}
                borderColor={borderColor}
                borderWidth="1px"
                shadow="lg"
              >
                <CardHeader>
                  <Flex justify="space-between" align="center">
                    <Heading size="md">Recent Activity</Heading>
                    <Badge colorScheme="blue" variant="subtle">
                      {activities.length} items
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  {activitiesLoading ? (
                    <VStack spacing={4} align="stretch">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} height="60px" />
                      ))}
                    </VStack>
                  ) : activities.length > 0 ? (
                    <VStack spacing={0} align="stretch">
                      <AnimatePresence>
                        {activities.map((activity, index) => (
                          <MotionBox
                            key={activity.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Box
                              p={4}
                              borderRadius="md"
                              _hover={{ bg: hoverBg, cursor: "pointer" }}
                              onClick={() => handleActivityClick(activity)}
                              transition="all 0.2s"
                            >
                              <Flex align="center" justify="space-between">
                                <HStack spacing={3} flex={1}>
                                  <Box
                                    p={2}
                                    borderRadius="lg"
                                    bg={`${getStatusColor(activity.status)}.100`}
                                    color={`${getStatusColor(activity.status)}.600`}
                                  >
                                    <Icon 
                                      as={getActivityIcon(activity.type)} 
                                      boxSize={4}
                                    />
                                  </Box>
                                  <VStack align="start" spacing={0} flex="1">
                                    <Text fontWeight="medium" noOfLines={1}>
                                      {activity.title}
                                    </Text>
                                    <HStack spacing={2}>
                                      <Text fontSize="sm" color={textColor}>
                                        {formatTimeAgo(activity.time)}
                                      </Text>
                                      {activity.user && (
                                        <>
                                          <Text fontSize="sm" color={textColor}>•</Text>
                                          <Text fontSize="sm" color={textColor}>
                                            {activity.user}
                                          </Text>
                                        </>
                                      )}
                                    </HStack>
                                  </VStack>
                                </HStack>
                                <HStack spacing={2}>
                                  <Badge 
                                    colorScheme={getStatusColor(activity.status)}
                                    size="sm"
                                  >
                                    {activity.status}
                                  </Badge>
                                  {activity.path && (
                                    <Menu>
                                      <MenuButton
                                        as={IconButton}
                                        icon={<FiMoreVertical />}
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <MenuList>
                                        <MenuItem
                                          icon={<FiEye />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleActivityClick(activity);
                                          }}
                                        >
                                          View Details
                                        </MenuItem>
                                      </MenuList>
                                    </Menu>
                                  )}
                                </HStack>
                              </Flex>
                            </Box>
                            {index < activities.length - 1 && <Divider />}
                          </MotionBox>
                        ))}
                      </AnimatePresence>
                    </VStack>
                  ) : (
                    <Box textAlign="center" py={8}>
                      <Icon as={FiClock} boxSize={12} color="gray.400" mb={4} />
                      <Text color={textColor} fontSize="lg" mb={2}>
                        No recent activity
                      </Text>
                      <Text color={textColor} fontSize="sm">
                        Start creating content to see activity here
                      </Text>
                    </Box>
                  )}
                </CardBody>
              </MotionCard>
            </GridItem>

            {/* Enhanced Quick Actions */}
            <GridItem>
              <VStack spacing={6} align="stretch">
                <MotionCard
                  variants={itemVariants}
                  bg={cardBg}
                  borderColor={borderColor}
                  borderWidth="1px"
                  shadow="lg"
                >
                  <CardHeader>
                    <Heading size="md">Quick Actions</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={3} align="stretch">
                      {quickActions.map((action, index) => (
                        <MotionBox
                          key={action.label}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            leftIcon={<Icon as={action.icon} />}
                            variant="outline"
                            justifyContent="start"
                            size="lg"
                            height="auto"
                            py={4}
                            onClick={() => handleQuickAction(action.path)}
                            colorScheme={action.color}
                            _hover={{ bg: `${action.color}.50` }}
                          >
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontWeight="semibold">{action.label}</Text>
                              <Text fontSize="sm" color={textColor}>
                                {action.description}
                              </Text>
                            </VStack>
                          </Button>
                        </MotionBox>
                      ))}
                    </VStack>
                  </CardBody>
                </MotionCard>

                {/* Documentation Links Card */}
                <MotionCard
                  variants={itemVariants}
                  bg={cardBg}
                  borderColor={borderColor}
                  borderWidth="1px"
                  shadow="lg"
                >
                  <CardHeader>
                    <Heading size="md">Documentation & Help</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={3} align="stretch">
                      {documentationActions.map((action, index) => (
                        <MotionBox
                          key={action.label}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            leftIcon={<Icon as={action.icon} />}
                            variant={action.variant}
                            justifyContent="start"
                            size="lg"
                            height="auto"
                            py={4}
                            onClick={() => handleQuickAction(action.path)}
                            colorScheme={action.color}
                            _hover={{ bg: `${action.color}.50` }}
                          >
                            <VStack align="start" spacing={0} flex={1}>
                              <Text fontWeight="semibold">{action.label}</Text>
                              <Text fontSize="sm" color={textColor}>
                                {action.description}
                              </Text>
                            </VStack>
                          </Button>
                        </MotionBox>
                      ))}
                    </VStack>
                  </CardBody>
                </MotionCard>

                {/* Progress/Goals Card */}
                <MotionCard
                  variants={itemVariants}
                  bg={cardBg}
                  borderColor={borderColor}
                  borderWidth="1px"
                  shadow="lg"
                >
                  <CardHeader>
                    <Heading size="md">This Week's Progress</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="sm">Content Created</Text>
                          <Text fontSize="sm" color={textColor}>7/10</Text>
                        </Flex>
                        <Progress value={70} colorScheme="blue" borderRadius="md" />
                      </Box>
                      <Box>
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="sm">Media Uploaded</Text>
                          <Text fontSize="sm" color={textColor}>12/15</Text>
                        </Flex>
                        <Progress value={80} colorScheme="green" borderRadius="md" />
                      </Box>
                      <Box>
                        <Flex justify="space-between" mb={2}>
                          <Text fontSize="sm">Models Updated</Text>
                          <Text fontSize="sm" color={textColor}>3/5</Text>
                        </Flex>
                        <Progress value={60} colorScheme="purple" borderRadius="md" />
                      </Box>
                    </VStack>
                  </CardBody>
                </MotionCard>
              </VStack>
            </GridItem>
          </Grid>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Home;